import type { ReactNode } from 'react';
import {
  REQUISITION_PRIORITY_LABELS,
  REQUISITION_STATUS_LABELS,
  SLA_STATE_LABELS,
  type ApprovalStepStatus,
  type RequisitionPriority,
  type RequisitionStatus,
  type SlaSummary,
} from '@nest-recruit/shared';
import { Dot } from '../../../components/Chip';
import { PRIORITY_TONES, REQUISITION_STATUS_TONES, SLA_TONES } from '../../../components/status-tones';
import { ToneChip, slaTone, type Tone } from '../../../components/Tone';
import { EMPTY } from '../../../lib/format';
import { useModule } from '../../../lib/modules';
import { slaFigures, slaLimit } from '../sla-format';
import styles from './RequisitionBadges.module.css';

/**
 * The tone of a requisition's card or row: a breached or warning clock wins over the status. Pass the clock only while Time limits is
 * on (`useModule('sla')`): an overdue clock must not turn a row red while the part is off (rule 13, bug B9).
 */
export function requisitionTone(status: RequisitionStatus, sla: SlaSummary | null): Tone {
  const tone = REQUISITION_STATUS_TONES[status];
  return sla ? slaTone(sla.state, tone) : tone;
}

/** An approval step's outcome, in words (REQ-03 and HM-01). */
export const APPROVAL_STEP_LABELS: Readonly<Record<ApprovalStepStatus, string>> = {
  pending: 'Not yet',
  approved: 'Approved',
  rejected: 'Rejected',
  sent_back: 'Sent back',
  skipped: 'Not needed',
};

export function RequisitionStatusChip({ status }: { readonly status: RequisitionStatus }): ReactNode {
  return <ToneChip tone={REQUISITION_STATUS_TONES[status]}>{REQUISITION_STATUS_LABELS[status]}</ToneChip>;
}

/** A dot, never a chip, so it never competes with the status beside it (Design System). */
export function PriorityDot({ priority }: { readonly priority: RequisitionPriority }): ReactNode {
  return <Dot tone={PRIORITY_TONES[priority]}>{REQUISITION_PRIORITY_LABELS[priority]}</Dot>;
}

/**
 * Both figures, always (master plan S9: "anyone disputing a number can see the
 * detail immediately"). The state is a dot; the figures are plain text beside it.
 *
 * Rule 13 (bug B9, 2026-11-02): a time limit belongs to Time limits (`sla`). While it is off this draws nothing at all — no state,
 * no figures, no limit, not even `—` — so "On track · limit 2.7 working days" never shows. A caller with a heading or a column for
 * it hides that too.
 */
export function SlaFigure({
  sla,
  showLimit = false,
}: {
  readonly sla: SlaSummary | null;
  readonly showLimit?: boolean;
}): ReactNode {
  const isOn = useModule('sla');
  if (!isOn) return null;
  if (!sla) return EMPTY;
  const limit = showLimit ? slaLimit(sla) : null;
  return (
    <span className={styles.sla} title={`${SLA_STATE_LABELS[sla.state]} — ${slaFigures(sla)}`}>
      <Dot tone={SLA_TONES[sla.state]}>{SLA_STATE_LABELS[sla.state]}</Dot>
      <span className={styles.figures}>
        {slaFigures(sla)}
        {limit !== null && <span className={styles.limit}> · {limit}</span>}
      </span>
    </span>
  );
}
